import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Header from '@/common/header';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';

import { Footer } from '../../footer';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { V1A_NAMESPACE } from './constants';
import { SignupForm } from './form/signup-form';
import { TrialDialog } from './form/trial-dialog';
import { useReveal } from './hooks/use-reveal';
import { AppShowcase } from './sections/app-section';
import { Close } from './sections/close';
import { Edge } from './sections/edge';
import { Faq } from './sections/faq';
import { Games } from './sections/games';
import { Hero } from './sections/hero';
import { Pros } from './sections/pros';
import { Shift } from './sections/shift';
import { Stats } from './sections/stats';
import { Tech } from './sections/tech';
import { V1aTrialContext } from './trial-context';
import styles from './v1a.module.css';

interface ProChampionsProps extends DefaultTrialProps {}

/** Same-page links that mean "sign me up": the design sends every one of them to the dialog. */
const TRIAL_HASH = /^#(cadastro|np-form)$/;
const TRIAL_PATH = /^\/(?:[a-z]{2}\/)?trial\/?$/i;

function isTrialLink(anchor: HTMLAnchorElement): boolean {
  const href = anchor.getAttribute('href') ?? '';
  if (TRIAL_HASH.test(href)) return true;
  try {
    const url = new URL(href, window.location.href);
    return url.origin === window.location.origin && TRIAL_PATH.test(url.pathname) && !url.search;
  } catch {
    return false;
  }
}

/**
 * v1a "Campeões mundiais eliminam o lag" (ProChampions). Ported from the standalone design
 * (nopingsite/v1a): the page styles live in CSS Modules scoped to this root, the Multiconexão and
 * Multi Internet cards are native components, and the design's sign-up card is wired to the real
 * `createTrial` flow (see form/signup-form.tsx).
 */
export function ProChampions({ trialConfigId, trialPageId, isDisabledButton = false }: ProChampionsProps) {
  const { t } = useTranslation(V1A_NAMESPACE);
  const rootRef = useRef<HTMLDivElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openTrial = useCallback(() => setIsDialogOpen(true), []);
  const closeTrial = useCallback(() => setIsDialogOpen(false), []);
  const trialContext = useMemo(() => ({ openTrial }), [openTrial]);

  useReveal(rootRef);

  // In-page trial links (`/trial`, `#cadastro`, `#np-form`) open the dialog instead of navigating to where the
  // visitor already is.
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a') : null;
      // Only the LP's own CTAs: the site header's "Teste grátis" keeps its site-wide behaviour (a link to /trial).
      if (!anchor || anchor.closest('.np-trial-dialog') || !rootRef.current?.contains(anchor) || !isTrialLink(anchor)) return;
      event.preventDefault();
      event.stopPropagation();
      openTrial();
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [openTrial]);

  const formProps = { trialConfigId, trialPageId, isDisabledButton };
  const fontVariable = { '--v1a-font': bai_jamjuree.style?.fontFamily || undefined } as CSSProperties;

  return (
    <V1aTrialContext.Provider value={trialContext}>
      <Header variant='primary' />
      <div ref={rootRef} className={classNames(styles.root, bai_jamjuree.className)} style={fontVariable}>
        <main id='conteudo' tabIndex={-1}>
          <Hero form={<SignupForm {...formProps} formId='v1a-hero-form' />} />
          <Stats />
          <Tech />
          <Shift />
          <AppShowcase />
          <Pros />
          <Edge />
          <Games />
          <Faq />
          <Close />
        </main>
        <p className='mx-auto my-4 px-[var(--gutter)] text-center text-[length:.72rem] text-[#9AA3B5] opacity-[.65]'>{t('footer.rating_source')}</p>
        <TrialDialog open={isDialogOpen} onClose={closeTrial}>
          <SignupForm {...formProps} formId='v1a-dialog-form' />
        </TrialDialog>
      </div>
      <Footer text={t('footer.copyright', { year: new Date().getFullYear() })} />
    </V1aTrialContext.Provider>
  );
}
